import React from "react";
import { Link } from "react-router-dom";
import { Ear, Lock, Handshake, MessageSquareHeart, ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import { Overline, CTABand } from "@/components/blocks";
import { WHY_CHOOSE, ASSETS } from "@/data/site";
import { ServiceIcon } from "@/components/blocks";
import DigitalVisit from "@/components/DigitalVisit";
import Testimonials from "@/components/Testimonials";

const PILLARS = [
  { icon: Ear, t: "Listening", d: "Consultations that begin by truly understanding you." },
  { icon: Lock, t: "Privacy", d: "A confidential, discreet and safe environment." },
  { icon: Handshake, t: "Respect & Comfort", d: "Warmth and dignity at every visit." },
  { icon: MessageSquareHeart, t: "Clear Communication", d: "Honest explanations and long-term relationships." },
];

export default function PatientExperience() {
  return (
    <div>
      <SEO title="Why Choose Us | Dr. Mithee's Clinic for Women, Noida" path="/patient-experience" description="A clinic where you feel heard. Experience compassionate, patient-centred women's healthcare with Dr. Mithee Bhanot in Noida." />

      <section className="bg-gradient-to-b from-blush to-background pt-28 md:pt-36">
        <div className="container-clinic grid items-center gap-10 pb-20 md:grid-cols-2">
          <Reveal>
            <Overline>Patient Experience</Overline>
            <h1 className="mt-4 font-serif text-4xl leading-tight tracking-tight text-charcoal sm:text-5xl">A Clinic Where You Feel Heard</h1>
            <p className="mt-6 text-base leading-relaxed text-taupe">
              Great care is as much about how you feel as what is done. Dr. Mithee's clinic is built around listening, privacy, respect and comfort — a place where you can ask questions freely and build a trusted, long-term relationship with your doctor.
            </p>
            <Link to="/appointment" data-testid="pe-book-btn" className="mt-8 inline-flex items-center gap-2 rounded-full bg-terracotta px-8 py-4 font-sans text-sm font-semibold text-cream transition-colors hover:bg-terracotta-dark">
              Book Your Consultation <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
          <Reveal delay={120} className="relative overflow-hidden rounded-3xl">
            <img src={ASSETS.clinicInterior} alt="Warm clinic reception" className="w-full object-cover shadow-card" />
          </Reveal>
        </div>
      </section>

      <section className="container-clinic py-20 md:py-28">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {PILLARS.map((p, i) => (
            <Reveal key={p.t} delay={(i % 4) * 80}>
              <div className="h-full rounded-3xl border border-soft-border bg-cream p-8 text-center">
                <span className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-blush text-terracotta"><p.icon className="h-6 w-6" /></span>
                <h3 className="mt-5 font-serif text-lg text-charcoal">{p.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-taupe">{p.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <DigitalVisit />

      <section className="bg-charcoal py-20 md:py-28">
        <div className="container-clinic">
          <Reveal><h2 className="text-center font-serif text-3xl text-cream md:text-4xl">Why Women Choose Dr. Mithee</h2></Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {WHY_CHOOSE.map((w, i) => (
              <Reveal key={w.title} delay={(i % 3) * 90}>
                <div className="h-full rounded-3xl border border-cream/10 bg-cream/[0.04] p-8">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-terracotta/20 text-rose"><ServiceIcon name={w.icon} className="h-5 w-5" /></span>
                  <h3 className="mt-5 font-serif text-xl text-cream">{w.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-cream/60">{w.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Testimonials />

      <CTABand />
    </div>
  );
}
